import { Range } from "@/hooks/api/search/useInfiniteSearch";

export const defaultHeadcountRanges: Range[] = [
  { min: 1, max: 10 },
  { min: 11, max: 50 },
  { min: 51, max: 200 },
  { min: 201, max: 500 },
  { min: 501, max: 1000 },
  { min: 1001, max: 5000 },
  { min: 5001, max: 10000 },
  { min: 10000 },
];

export const defaultRevenueRanges: Range[] = [
  { max: 1_000_000 },
  { min: 1_000_000, max: 5_000_000 },
  { min: 5_000_000, max: 10_000_000 },
  { min: 10_000_000, max: 50_000_000 },
  { min: 50_000_000, max: 100_000_000 },
  { min: 100_000_000 },
];

type BaseLabelOptions = {
  formatter?: Intl.NumberFormat;
  suffix?: string;
  joiner?: string;
  plusSuffix?: string;
  noFromPrefix?: string;
  noToPrefix?: string;
  divideNumbersBy?: number;
  abbreviate?: boolean;
};

const removeLastDotAndWhitespace = (input: string): string => {
  const reversed = input.split("").reverse().join("");
  const withoutDot = reversed.replace(".", "");
  const withoutWhitespace = withoutDot.replace(/\s/, "");
  return withoutWhitespace.split("").reverse().join("");
};

const formatNumber = (n: number, abbreviate: boolean, formatter = new Intl.NumberFormat()): string => {
  const finalFormatter = abbreviate
    ? new Intl.NumberFormat(navigator.language, { notation: "compact", compactDisplay: "short" })
    : formatter;

  let number = finalFormatter.format(n).toLowerCase();
  if (abbreviate && navigator.language.startsWith("de")) {
    if (Math.abs(n) < 1_000_000) {
      number = `${Math.round(n / 1000)}k`;
    } else {
      number = removeLastDotAndWhitespace(number);
    }
  }
  return number;
};

export const formatCurrency = (n: number): string => {
  return `${formatNumber(n, true)} €`;
};

const baseRangeLabel = (
  from: number | null | undefined,
  to: number | null | undefined,
  {
    formatter = new Intl.NumberFormat(),
    suffix = "",
    joiner = ` ${String.fromCharCode(0x2013)} `,
    plusSuffix = " +",
    noFromPrefix = "< ",
    noToPrefix = "> ",
    abbreviate = false,
  }: BaseLabelOptions = {},
): string => {
  // Handle negative ranges: If the lower bound is negative and the upper bound is (effectively) zero,
  if (from !== null && from !== undefined && from < 0 && to !== null && to !== undefined && Math.abs(to) <= 1) {
    return `${noToPrefix}${formatNumber(from, abbreviate, formatter)}${suffix}`;
  }

  if ((from === null || from === undefined || from === 0) && to !== null && to !== undefined) {
    return `${noFromPrefix}${formatNumber(to, abbreviate, formatter)}${suffix}`;
  }

  if ((to === null || to === undefined || to === 0) && from !== null && from !== undefined) {
    return `${noToPrefix}${formatNumber(from, abbreviate, formatter)}${suffix}`;
  }

  const formattedFrom = formatNumber(from!, abbreviate, formatter);
  if (to !== null && to !== undefined) {
    return `${formattedFrom}${suffix}${joiner}${formatNumber(to, abbreviate, formatter)}${suffix}`;
  }
  return `${formattedFrom}${suffix}${plusSuffix}`;
};

export const headCountRangeLabels = (from?: number | null, to?: number | null) => baseRangeLabel(from, to);

export const currencyLabels = (from?: number | null, to?: number | null) =>
  baseRangeLabel(from, to, {
    suffix: " €",
    abbreviate: true,
  });
